/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, useSortBy, usePagination, useFilters, useExpanded } from 'react-table'
import { Input, Select } from 'components/form'
import { TableWrapper } from 'components/common/ReportTable'
import { Link, FlexView, Card, Icon } from 'components/common'
import { FieldDataContext } from 'stores/FieldDataStore'
import { UserContext } from 'stores/UserStore'
import styled from 'styled-components'
import moment from 'moment'
import _ from 'lodash'
import { useHistory } from 'react-router-dom'


const Table = styled(TableWrapper)`
  display: flex;
  flex-direction: column;

  .table-wrap {
    flex: 1;
    overflow: auto;
  }

  th input {
    font-size: ${({ theme }) => theme.fontSizes.small};
    padding: 4px;
    min-height: 0px;
  }
`

const DefaultColumnFilter = ({ column: { filterValue, preFilteredRows, setFilter }}) => {
  const onChange = useCallback(e => {
    setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
  }, [setFilter])

  return (
    <Input
      value={filterValue || ''}
      onChange={onChange}
      placeholder= "Type to Filter"
      margin="0px"
      width="100%"
    />
  )
}

const sortByDate = (rowA, rowB, columnId, desc) => {
  const valueA = _.get(rowA, `original.${columnId}`)
  const valueB = _.get(rowB, `original.${columnId}`)
  return moment.utc(valueA).isAfter(valueB) ? 1 : -1
}

const tableOptions = {
  defaultColumn: {
    Filter: DefaultColumnFilter
  },
  getSubRows: row => row.children || [],
  paginateExpandedRows: false,
  autoResetExpanded: false,
  autoResetPage: false,
  sortTypes: {
    date: sortByDate
  }
}

const FieldDataTable = () => {
  const DEFAULT_URL = "field-data"
  const history = useHistory()
  const { t } = useTranslation()
  const { filteredFieldData: { tableData }, setSelectedItem } = useContext(FieldDataContext)
  const { registryLog } = useContext(UserContext)

  const data = useMemo(() => (
    _.chain(tableData).value()
  ), [tableData])


 const onShow = useCallback((item) => () => {
    setSelectedItem(item)
    history.push('/field-data/tags')
  }, [history])    

  const columns = useMemo(() => {
    const result = []
    result.push({
      Header: t('fieldData.name'),
      accessor: 'name',
      Cell: ({ cell: { row } }) => (
        <FlexView flexDirection="row" alignItems="center" justifyContent="flex-start">
          <div style={{ textAlign: 'left', fontWeight: 400, fontSize: '13px'}}>{row.values.name}</div>        
        </FlexView>
      ),      
      customHeaderProps: { style: { width: 'auto' } },
      customCellProps: { style: { width: 'auto' } },
      disableFilters: true
    })
    result.push({
      Header: t('fieldData.type'),
      accessor: 'type',
      customHeaderProps: { style: { width: 'auto' } },
      customCellProps: { style: { width: 'auto' } },
      disableFilters: true
    })
    result.push({
      Header: t('fieldData.localization'),
      accessor: 'localization',
      customHeaderProps: { style: { width: 'auto' } },
      customCellProps: { style: { width: '100px' } },
      disableFilters: true
    })
    result.push({
        Header: t('fieldData.created'),
        accessor: 'created',
        // Cell: ({ cell: { value } }) => value ? moment.utc(value).format('DD/MM/YYYY') : '',
        customHeaderProps: { style: { width: 'auto' } },
        customCellProps: { style: { width: '100px' } },
        disableFilters: true
    })
    result.push({
      Header: t('fieldData.tags'),
      accessor: 'tags',
      Cell: ({ cell: { row } }) => (
        <FlexView flexDirection="row" alignItems="center" justifyContent="space-between">
          <div style={{ textAlign: 'left', fontWeight: 400, fontSize: '13px'}}>{row.values.tags.length} {t('fieldData.tags')} {t('fieldData.read')}</div> 
          <Icon name="arrow-right" color="primary" margin="2px 15px" width="16px" height="16px" title={t('fieldData.showTags')} onClick={onShow(row.values)}></Icon>
        </FlexView>
      ),      
      customHeaderProps: { style: { width: 'auto' } },
      customCellProps: { style: { width: '150px' } },
      disableFilters: true
    })
  
    return result
  }, [t])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      ...tableOptions
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
  )

  const navigationTable = useCallback((action, lastPage) => () => {
    //registryLogOld(`Go to ${action} page` , DEFAULT_URL)
    registryLog(DEFAULT_URL, 'pagining', action)
    if (action === 'first') {
      gotoPage(0)
    } else if (action === 'previous') {
      previousPage()
    } else if (action === 'next') {
      nextPage()
    } else if (action === 'last') {
      gotoPage(lastPage)
    }
  }, [])
  
  
  return (
    <Card padding="0px" margin="0px 16px 16px" alignSelf="stretch">
      <Table elevation="none" width="100%" fontSize="13px">
        <div className="table-wrap">
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps(column.customHeaderProps)} className="sortable">
                      <div {...column.getSortByToggleProps({ title: '' })}>
                        {column.render('Header')}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? <svg width="13" height="7" viewBox="0 0 13 7" fill="none" xmlns="http://www.w3.org/2000/svg" transform='rotate(180)'>
                                  <path d="M0 6.5L6.5 6.5L13 6.5L6.5 0L0 6.5Z" fill="#777777"/>
                                </svg>
                              : <svg width="13" height="7" viewBox="0 0 13 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M0 6.5L6.5 6.5L13 6.5L6.5 0L0 6.5Z" fill="#777777"/>
                                </svg>
                            : ''}
                        </span>
                      </div>
                      <div>{column.canFilter ? column.render('Filter') : null}</div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps({ style: { backgroundColor: !row.canExpand ? '#f5f5f5' : 'inherit' }})}>
                    {row.cells.map(cell => {
                      return <td {...cell.getCellProps(cell.column.customCellProps)}>{cell.render('Cell')}</td>
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <FlexView flexDirection="row" justifyContent="flex-start" alignItems="center" flex="1">
            <Input
              inline
              type="number"
              label={t('Go to page')}
              fontSize="13px"
              margin="0px"
              padding="8px 4px"
              defaultValue={pageIndex + 1}
              onChange={e => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0
                //registryLogOld('Go to Page ' + (page+1) , DEFAULT_URL)
                registryLog(DEFAULT_URL, 'pagining', page + 1)
                gotoPage(page)
              }}
              width="180px"
            />
          </FlexView>
          <FlexView flexDirection="row" alignItems="center" justifyContent="center" fontSize="13px">
            <Link noDecoration color="gray" fontSize="big" fontWeight="bold" padding="4px" margin="0px 4px" onClick={navigationTable('first')} disabled={!canPreviousPage}>
              {'«'}
            </Link>{' '}
            <Link noDecoration color="gray" fontSize="big" fontWeight="bold" padding="4px" margin="0px 4px" onClick={navigationTable('previous')} disabled={!canPreviousPage}>
              {'‹'}
            </Link>{' '}
            <FlexView flexDirection="row" margin="0px 8px" fontWeight="bold">
              {pageIndex + 1} {t('of')} {pageOptions.length}
            </FlexView>
            <Link noDecoration color="gray" fontSize="big" fontWeight="bold" padding="4px" margin="0px 4px" onClick={navigationTable('next')} disabled={!canNextPage}>
              {'›'}
            </Link>{' '}
            <Link noDecoration color="gray" fontSize="big" fontWeight="bold" padding="4px" margin="0px 4px" onClick={navigationTable('last', pageCount - 1)} disabled={!canNextPage}>
              {'»'}
            </Link>{' '}
          </FlexView>
          <FlexView flex="1" flexDirection="row" justifyContent="flex-end" alignItems="center">
            <FlexView flexDirection="row" alignItems="center" fontSize="small" borderRadius="component" backgroundColor="whitesmoke" padding="8px" margin="0px 16px">
              <FlexView margin="0px 4px 0px" color="secondary">{data.length}</FlexView>
              <span>{t('fieldData.pageSubtitle')}</span>
            </FlexView>
            <Select
              margin="0px"
              value={pageSize}
              options={
                [5, 10, 20, 30, 40, 50].map(pageSize => ({
                  value: pageSize,
                  label: `${t('Show')} ${pageSize}`
                }))
              }
              onChange={value => {
                //registryLogOld('Show ' + Number(value), DEFAULT_URL)
                registryLog(DEFAULT_URL, 'show_pagining', value)
                setPageSize(Number(value))
              }}
            />
          </FlexView>
        </div>
      </Table>
    </Card>
  )
}

export default FieldDataTable
